import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AlreadyFramedArtworkIcon from '@/assets/all-icons/already-framed-artwork-icon.jsx';
import CertificateOfAuthenticityIcon from '@/assets/all-icons/certificate-of-authenticity-icon.jsx';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import usePreferredLocale from '@/hooks/usePreferredLocale';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import { ArtworkOfferPrice } from '@/pages/artwork/components/ArtworkOfferElements';
import {
  buildArtist,
  buildLimitedEditionHoldingOwners,
  buildLimitedEditionOwners,
  buildMetadataSoldHistoryFallback,
  buildOwnershipSectionData,
  formatSoldHistoryDate,
  formatSoldHistoryPrice,
  getSoldHistory,
  selectPreferredSoldHistoryEntries,
  splitSoldHistoryEntries,
} from '@/pages/artwork/content-section/components/artworkMoreDetailsDesktop.helpers';
import AudioPlayer from '@/pages/artwork/content-section/components/AudioPlayer';
import Description from '@/pages/artwork/content-section/components/Description';
import LEsOwnershipCard from '@/pages/artwork/content-section/components/LEsOwnershipCard';
import { resolveLimitedEditionContext } from '@/pages/artwork/content-section/utils/limitedEdition.utils';
import { getLocalizedArtworkMedium } from '@/pages/artwork/utils/artworkMediumDisplay.utils';
import { formatArtworkTitleForDisplay } from '@/pages/artwork/utils/artworkTitleDisplay.utils';
import {
  getArtworkArtistModalData,
  getArtworkHoldingType,
  getArtworkLimitedEditionHoldings,
  getArtworkLimitedEditionOptions,
  getArtworkTransactionSource,
  getArtworkViewerContext,
  isViewerArtworkGalleryOwner,
} from '@/pages/artwork/utils/artworkView.selectors';
import { parseOfferAmount, resolveActiveViewerOffer } from '@/pages/artwork/utils/offer.utils';
import ArtworksOfArtist from '@/pages/profile/artists/artworks-of-artist';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import LimitedEditionSummary from '@/shared-components/artwork/limited-edition-summary';
import SellInStorePopup from '@/shared-components/artwork/sell-in-store/SellInStorePopup';
import FollowButton from '@/shared-components/buttons/components/FollowButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import { isRestrictedArtwork } from '@/utils/artworkVisibility';
import { resolveLocalizedPublicPath } from '@/utils/localePublicUrl';
import NumberUtils from '@/utils/number.utils';

import './ArtworkMoreDetails.css';

function PrivateOwnerIndicator({ label }) {
  return (
    <div className="artwork-more-details__owner-private">
      <svg
        className="artwork-more-details__owner-lock-icon"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <rect x="3.5" y="8.5" width="13" height="9" rx="3.5" ry="3.5" />
        <path d="M6.5 8.5v-2.2a3.5 3.5 0 1 1 7 0v2.2" />
        <circle cx="10" cy="12.5" r="1.35" />
      </svg>
      <span className="artwork-more-details__owner-private-label">{label}</span>
    </div>
  );
}

// Bottom-sheet content with extended artwork information for mobile.
export default function ArtworkMoreDetails({
  artworkData: artworkDataProp,
  onOpenPurchase = null,
  onOpenLimitedEdition: onOpenLimitedEditionProp = null,
  onOpenLimitedEditionCollectors = null,
}) {
  const t = useTranslations('common.shared.artwork_view');
  const locale = usePreferredLocale();
  const context = useContext(ArtworkDataContext);
  const baseArtworkData = artworkDataProp || context.artworkData;
  const [artworkDataOverride, setArtworkDataOverride] = useState(null);

  const artworkIdentity = useMemo(() => (
    baseArtworkData?._id
    ?? baseArtworkData?.product_id
    ?? baseArtworkData?.slug
    ?? null
  ), [baseArtworkData]);

  useEffect(() => {
    setArtworkDataOverride(null);
  }, [artworkIdentity]);

  const artworkData = artworkDataOverride ?? baseArtworkData;
  const setArtworkData = useCallback((value) => {
    setArtworkDataOverride((previousOverride) => {
      const previousResolved = previousOverride ?? baseArtworkData;
      return typeof value === 'function' ? value(previousResolved) : value;
    });
  }, [baseArtworkData]);

  const navigate = useNavigate();
  const { slug } = useParams();
  const { state } = useStateHandler();
  const [sellInStoreOpen, setSellInStoreOpen] = useState(false);
  const [limitedEditionBlockedPopupOpen, setLimitedEditionBlockedPopupOpen] = useState(false);

  const limitedEditionContext = useMemo(
    () => resolveLimitedEditionContext(artworkData),
    [artworkData],
  );
  const productMetadata = limitedEditionContext?.metadata ?? {};
  const isLimitedEditionProduct = limitedEditionContext.isLimitedEdition;
  const editionNumber = limitedEditionContext.editionNumber;
  const holdingType = getArtworkHoldingType(artworkData);
  const isLimitedEditionHolding = holdingType === 'limited_edition';
  const limitedEditionOptions = useMemo(
    () => getArtworkLimitedEditionOptions(artworkData, { fallbackHoldingMetadata: isLimitedEditionHolding }),
    [artworkData, isLimitedEditionHolding],
  );
  const openCollectors = onOpenLimitedEditionCollectors ?? context.openLimitedEditionCollectors;
  const [showPurchaseBlockedPopup, setShowPurchaseBlockedPopup] = useState(false);
  const isGalleryMember = state.user_session?.user_type !== "gallery"
    && Boolean(state.user_session?.gallery_id);

  const artworkAbout = artworkData?.artwork_about ?? {};
  const artworkAudio = artworkData?.artwork_media?.artwork_audio?.audio_id ?? null;

  const {
    artwork_title,
    artwork_medium,
    artwork_size,
    artwork_year,
    artwork_includes_certificate,
    artwork_includes_frame,
    artwork_price,
    artwork_gallery,
    artwork_type,
    artwork_currency,
  } = artworkAbout;
  const displayArtworkTitle = formatArtworkTitleForDisplay(artwork_title) || '-';
  const localizedArtworkMedium = getLocalizedArtworkMedium(artwork_medium, locale);

  const formattedSize = artwork_size
    ? `${artwork_size.length} x ${artwork_size.height} ${artwork_size.unit}`.trim()
    : '-';

  const currencyCode = artwork_currency ?? 'USD';
  const formattedPrice = useMemo(() => {
    if (typeof artwork_price === 'number' && Number.isFinite(artwork_price)) {
      return NumberUtils.getArtworkPrice(artwork_price, currencyCode);
    }
    if (typeof artwork_price === 'string' && artwork_price.trim().length > 0) {
      return artwork_price;
    }
    return '-';
  }, [artwork_price, currencyCode]);
  const activeOffer = resolveActiveViewerOffer(artworkData);
  const activeOfferPrice = parseOfferAmount(activeOffer?.offer_price);
  const viewerContext = getArtworkViewerContext(artworkData);

  const artworkTypeLabel = artwork_type
    ?? (artworkAbout?.artwork_limited_edition ? 'Limited edition' : 'Unique work');

  const artist = useMemo(
    () => buildArtist(artworkAbout, viewerContext),
    [artworkAbout, viewerContext?.artist, viewerContext?.gallery_artist],
  );

  const artistModalData = useMemo(() => getArtworkArtistModalData(artworkData), [artworkData]);

  const limitedEditionEntries = useMemo(
    () => Object.entries(limitedEditionOptions || {}).filter(([, entry]) => entry),
    [limitedEditionOptions],
  );
  const hasLimitedEditionAvailable = useMemo(() => {
    return limitedEditionEntries.some(([, edition]) => {
      const { quantity, sold } = edition;
      if (typeof quantity === 'number' && typeof sold === 'number') {
        return quantity > sold;
      }
      return true;
    });
  }, [limitedEditionEntries]);

  const shouldRenderLimitedEditionSummary =
    limitedEditionEntries.length > 0 || hasLimitedEditionAvailable || isLimitedEditionHolding;
  const isAdminOnly = useMemo(() => isRestrictedArtwork(artworkData), [artworkData]);
  const transactionHistoryEntries = useMemo(() => {
    const src = getArtworkTransactionSource(artworkData);
    return getSoldHistory(src, { artworkData });
  }, [artworkData]);

  const metadataSoldHistoryEntries = useMemo(
    () => buildMetadataSoldHistoryFallback(artworkData),
    [artworkData],
  );

  const { limitedEdition: limitedEditionSoldHistoryEntries } = useMemo(
    () => splitSoldHistoryEntries(transactionHistoryEntries),
    [transactionHistoryEntries],
  );

  const {
    limitedEdition: metadataLimitedEditionSoldHistoryEntries,
  } = useMemo(
    () => splitSoldHistoryEntries(metadataSoldHistoryEntries),
    [metadataSoldHistoryEntries],
  );

  const limitedEditionHoldingsRaw = useMemo(
    () => getArtworkLimitedEditionHoldings(artworkData),
    [artworkData],
  );

  const limitedEditionHoldingOwners = useMemo(
    () => buildLimitedEditionHoldingOwners(limitedEditionHoldingsRaw),
    [limitedEditionHoldingsRaw],
  );

  const buildOwnerKey = useCallback((owner) => {
    if (!owner) {
      return '';
    }
    const identity = owner.username ?? owner.name ?? owner.id ?? '';
    const editionKey = owner.editionNumber ?? owner.editionSize ?? '';
    return `${identity}-${editionKey}`;
  }, []);

  const limitedEditionOwners = useMemo(() => {
    let historyOwners = [];
    if (limitedEditionSoldHistoryEntries.length > 0) {
      historyOwners = buildLimitedEditionOwners(limitedEditionSoldHistoryEntries);
    } else if (metadataLimitedEditionSoldHistoryEntries.length > 0) {
      historyOwners = buildLimitedEditionOwners(metadataLimitedEditionSoldHistoryEntries);
    }

    if (!limitedEditionHoldingOwners.length) {
      return historyOwners;
    }
    if (!historyOwners.length) {
      return limitedEditionHoldingOwners;
    }

    const dedupeSet = new Set(
      historyOwners.map((owner) => owner.id ?? buildOwnerKey(owner)),
    );

    const merged = [...historyOwners];
    limitedEditionHoldingOwners.forEach((owner) => {
      const dedupeKey = owner.id ?? buildOwnerKey(owner);
      if (!dedupeSet.has(dedupeKey)) {
        merged.push(owner);
        dedupeSet.add(dedupeKey);
      }
    });

    return merged;
  }, [
    limitedEditionSoldHistoryEntries,
    metadataLimitedEditionSoldHistoryEntries,
    limitedEditionHoldingOwners,
  ]);

  const limitedEditionOwnersFiltered = useMemo(
    () => limitedEditionOwners.filter(
      (owner) =>
        owner?.isLimitedEdition === true
        || Number.isFinite(owner?.editionNumber)
        || Number.isFinite(owner?.editionSize),
    ),
    [limitedEditionOwners],
  );

  const [isArtistArtworksOpen, setIsArtistArtworksOpen] = useState(false);
  const [modalQueryData, setModalQueryData] = useState({ cacheKey: '', endpoint: '' });

  const cacheKeyTpl = 'artworks_of_{0}_in_search';
  const endpointArtist = '/galleryartist/{0}/artworks';
  const endpointGalleryArtist = '/galleryartist/{0}/artworks';

  const renderCertificateOwnershipLink = (label) => (
    <a
      href={resolveLocalizedPublicPath('/certificate-of-ownership', locale)}
      target="_blank"
      rel="noopener noreferrer"
      className="artwork-more-details__extra-info-link"
    >
      {label}
    </a>
  );

  const openArtistArtworks = () => {
    if (!artistModalData || !artistModalData._id) return;

    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.ARTIST_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'profile-row (within artwork-more-details__container)' },
      });
    }

    const forcedType = artistModalData.user_type === 'gallery_artist' ? 'gallery_artist' : 'artist';
    const cacheKey = cacheKeyTpl.replace('{0}', artistModalData._id);
    const endpoint =
      forcedType === 'gallery_artist'
        ? endpointGalleryArtist.replace('{0}', artistModalData._id)
        : endpointArtist.replace('{0}', artistModalData._id);

    setModalQueryData({ cacheKey, endpoint });
    setIsArtistArtworksOpen(true);
  };

  const handleArtistKeyDown = (event) => {
    if (!artistModalData) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openArtistArtworks();
    }
  };

  const handleOwnershipProfileClick = () => {
    if (!canNavigateToOwnershipProfile) {
      return;
    }

    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.GALLERY_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'profile-row (within artwork-more-details__container)' },
      });
    }

    navigate(`/user/${ownershipUsername}`);
  };

  const handleOwnershipProfileKeyDown = (event) => {
    if (!canNavigateToOwnershipProfile) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOwnershipProfileClick();
    }
  };

  const isOwnArtwork = isViewerArtworkGalleryOwner(artworkData, state?.user_session ?? {});
  const isAuctionArtwork = Boolean(artworkData?.artwork_about?.artwork_is_auction);
  const ownershipInfo = useMemo(
    () => buildOwnershipSectionData(artworkData, state?.user_session ?? {}, viewerContext),
    [
      artworkAbout,
      artworkData?.artwork_status,
      artworkData?.artwork_flags,
      artworkData?.ownership_context,
      state?.user_session,
      viewerContext?.gallery,
    ],
  );
  const ownershipHeadingKey = ownershipInfo?.headingKey ?? 'gallery';
  const ownershipProfile = ownershipInfo?.profile ?? artwork_gallery ?? null;
  const canFollowOwnership = ownershipInfo?.showFollowButton === true;
  const ownershipFallbackName = ownershipInfo?.fallbackName ?? null;
  const isPrivateOwner = ownershipInfo?.isPrivateOwner ?? false;
  const privateOwnerLabel = t('details.private_owner');
  const isAwaitingOwnershipIdentity = ownershipInfo?.isAwaitingOwnershipIdentity === true;
  const isOwnerView = ownershipInfo?.isOwnerView ?? false;
  const isOriginalProduct = ownershipInfo?.isOriginalProduct ?? !limitedEditionContext.isLimitedEdition;
  const isSold = ownershipInfo?.isSold ?? false;
  const canPurchase = !isAuctionArtwork && !isSold;
  const limitedEditionDisabled = isOwnArtwork || isAdminOnly;
  const limitedEditionDisabledMessage = limitedEditionDisabled
    ? t('details.limited_edition_disabled_message')
    : null;
  const ownershipHeadingDisplay = isLimitedEditionHolding
    ? (ownershipHeadingKey === 'gallery'
      ? t('details.ownership_heading.uploaded_by')
      : t('details.ownership_heading.original_artwork_owner'))
    : t(`details.headings.${ownershipHeadingKey}`);
  const ownershipDisplayName = isAwaitingOwnershipIdentity
    ? ''
    : (
      ownershipProfile?.name
      ?? ownershipProfile?.username
      ?? ownershipFallbackName
      ?? 'ƒ?"'
    );
  const ownershipLocation = ownershipProfile?.location ?? '';
  const ownershipProfileImage = ownershipProfile?.image ?? null;
  const ownershipProfileType = ownershipProfile?.type
    ?? (ownershipHeadingKey === 'gallery' ? 'gallery' : 'collector');
  const ownershipUserId = ownershipProfile?.id ?? null;
  const ownershipUsername = ownershipProfile?.username ?? '';
  const ownershipImFollowing = ownershipProfile?.imFollowing ?? false;
  const ownershipIsFollowingMe = ownershipProfile?.isFollowingMe ?? false;
  const canNavigateToOwnershipProfile = Boolean(ownershipUsername);
  const viewerDisplayName = state?.user_session?.user_name
    ?? state?.user_session?.user_username
    ?? null;
  const viewerId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const viewerUsername = state?.user_session?.user_username ?? null;

  const isOriginalPublicOwnerProfile = useMemo(
    () => (isOriginalProduct && ownershipHeadingKey === 'owner' && isPrivateOwner !== true),
    [isOriginalProduct, isPrivateOwner, ownershipHeadingKey],
  );
  const soldVia = typeof artworkData?.artwork_flags?.sold_via === 'string'
    ? artworkData.artwork_flags.sold_via.trim().toLowerCase()
    : '';
  const unclaimedOwner = Boolean(
    productMetadata?.unclaimed_owner === true
    || productMetadata?.unclaimed_email
  );
  const pendingClaim = Boolean(
    artworkData?.artwork_flags?.claim_code
    || artworkData?.artwork_flags?.claim_email
    || artworkData?.artwork_flags?.sold_contact?.email
    || artworkData?.artwork_flags?.sold_contact?.id
    || soldVia === 'in_store_suarte'
    || unclaimedOwner,
  );
  const canUseMetadataSoldHistory = isOwnerView || isOriginalPublicOwnerProfile || pendingClaim;

  const limitedEditionSaleFallback = useMemo(() => {
    if (!isLimitedEditionProduct) {
      return null;
    }

    const normalizedEditionNumber = Number(editionNumber);
    const targetEditionNumber = Number.isFinite(normalizedEditionNumber)
      ? normalizedEditionNumber
      : null;

    const historyCandidates = limitedEditionSoldHistoryEntries.length > 0
      ? limitedEditionSoldHistoryEntries
      : (isOwnerView ? metadataLimitedEditionSoldHistoryEntries : []);

    const pickMatch = (entries) => {
      if (!Array.isArray(entries) || entries.length === 0) {
        return null;
      }
      if (targetEditionNumber !== null) {
        const matched = entries.find(
          (entry) => Number(entry?.editionNumber) === targetEditionNumber,
        );
        if (matched) {
          return matched;
        }
      }
      return entries[0] ?? null;
    };

    const historyMatch = pickMatch(historyCandidates);
    if (historyMatch) {
      return {
        price:
          historyMatch.price
          ?? historyMatch.priceBreakdown?.net
          ?? historyMatch.priceBreakdown?.gross
          ?? null,
        currency: historyMatch.currency ?? artwork_currency ?? null,
        soldAt: historyMatch.date ?? historyMatch.dateRaw ?? null,
      };
    }

    const holdingMatch = (() => {
      if (!limitedEditionHoldingOwners.length) {
        return null;
      }
      if (targetEditionNumber !== null) {
        const exact = limitedEditionHoldingOwners.find(
          (owner) => Number(owner?.editionNumber) === targetEditionNumber,
        );
        if (exact) {
          return exact;
        }
      }
      return limitedEditionHoldingOwners[0] ?? null;
    })();

    if (holdingMatch) {
      return {
        price: holdingMatch.price ?? null,
        currency: holdingMatch.currency ?? artwork_currency ?? null,
        soldAt: holdingMatch.date ?? null,
      };
    }

    return null;
  }, [
    artwork_currency,
    editionNumber,
    isLimitedEditionProduct,
    isOwnerView,
    limitedEditionHoldingOwners,
    limitedEditionSoldHistoryEntries,
    metadataLimitedEditionSoldHistoryEntries,
  ]);
  const limitedEditionFallbackPrice = useMemo(() => ({
    amount: limitedEditionSaleFallback?.price ?? null,
    currency: limitedEditionSaleFallback?.currency ?? null,
  }), [
    limitedEditionSaleFallback?.currency,
    limitedEditionSaleFallback?.price,
  ]);
  const limitedEditionSoldAtFallback = limitedEditionSaleFallback?.soldAt ?? null;
  const limitedEditionIsPrivate = (productMetadata?.is_private ?? false) === true;
  const limitedEditionOwnerFallbackName = useMemo(() => {
    const candidate =
      limitedEditionHoldingOwners[0]
      ?? limitedEditionOwnersFiltered[0]
      ?? null;
    return candidate?.name ?? candidate?.username ?? ownershipDisplayName ?? '—';
  }, [limitedEditionHoldingOwners, limitedEditionOwnersFiltered, ownershipDisplayName]);
  const limitedEditionOwnerFallbackImage = useMemo(() => {
    const candidate =
      limitedEditionHoldingOwners[0]
      ?? limitedEditionOwnersFiltered[0]
      ?? null;
    return candidate?.imageId ?? candidate?.image_id ?? candidate?.image ?? null;
  }, [limitedEditionHoldingOwners, limitedEditionOwnersFiltered]);
  const limitedEditionOwnerFallbackType = useMemo(() => {
    const candidate =
      limitedEditionHoldingOwners[0]
      ?? limitedEditionOwnersFiltered[0]
      ?? null;
    return candidate?.type ?? candidate?.profile?.type ?? candidate?.profile?.user_type ?? null;
  }, [limitedEditionHoldingOwners, limitedEditionOwnersFiltered]);
  const limitedEditionOwnerIsViewer = useMemo(() => {
    const ownerCandidate =
      limitedEditionHoldingOwners[0]?.profile
      ?? limitedEditionHoldingOwners[0]
      ?? null;

    if (!ownerCandidate) {
      return false;
    }

    if (typeof ownerCandidate === 'string') {
      return Boolean(
        (viewerId && ownerCandidate === viewerId)
        || (viewerUsername && ownerCandidate === viewerUsername),
      );
    }

    const ownerId = ownerCandidate._id ?? ownerCandidate.id ?? null;
    const ownerUsername = ownerCandidate.username ?? ownerCandidate.user_username ?? null;

    return Boolean(
      (viewerId && ownerId && ownerId === viewerId)
      || (viewerUsername && ownerUsername && ownerUsername === viewerUsername),
    );
  }, [limitedEditionHoldingOwners, viewerId, viewerUsername]);
  const limitedEditionBadgeOverride = useMemo(() => {
    if (!isAdminOnly || !isLimitedEditionHolding || !limitedEditionHoldingOwners.length) {
      return null;
    }

    const normalizeSizeKey = (value) =>
      (typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '_') : null);

    const formatSizeLabel = (value) => {
      if (typeof value !== 'string') {
        return null;
      }
      const cleaned = value.trim().replace(/[_-]+/g, ' ');
      if (!cleaned) {
        return null;
      }
      return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const holdingId =
      artworkData?.metadata?.holding_id
      ?? artworkData?.product_metadata?.holding_id
      ?? artworkData?.holding_id
      ?? null;

    const matchOwner =
      limitedEditionHoldingOwners.find((owner) => holdingId && owner?.id === holdingId)
      ?? limitedEditionHoldingOwners.find(
        (owner) => Number.isFinite(Number(editionNumber))
          && Number(owner?.editionNumber) === Number(editionNumber),
      )
      ?? limitedEditionHoldingOwners[0];

    const sizeKey =
      normalizeSizeKey(matchOwner?.sizeKey)
      ?? normalizeSizeKey(matchOwner?.sizeLabel)
      ?? normalizeSizeKey(matchOwner?.size_label)
      ?? normalizeSizeKey(matchOwner?.size)
      ?? null;

    const ownersInGroup = sizeKey
      ? limitedEditionHoldingOwners.filter((owner) => {
        const ownerKey =
          normalizeSizeKey(owner?.sizeKey)
          ?? normalizeSizeKey(owner?.sizeLabel)
          ?? normalizeSizeKey(owner?.size_label)
          ?? normalizeSizeKey(owner?.size)
          ?? null;
        return ownerKey === sizeKey;
      })
      : limitedEditionHoldingOwners;

    if (!ownersInGroup.length) {
      return null;
    }

    const indexInGroup = ownersInGroup.findIndex((owner) => owner?.id === matchOwner?.id);
    const sequenceNumber = indexInGroup >= 0 ? indexInGroup + 1 : 1;
    const sequenceTotal = ownersInGroup.length;
    const sizeLabel = formatSizeLabel(matchOwner?.sizeLabel ?? matchOwner?.sizeKey ?? matchOwner?.size);
    if (sizeLabel) {
      return `${sizeLabel} ${sequenceNumber}/${sequenceTotal}`;
    }
    return `${sequenceNumber}/${sequenceTotal}`;
  }, [
    artworkData?.holding_id,
    artworkData?.metadata?.holding_id,
    artworkData?.product_metadata?.holding_id,
    editionNumber,
    isAdminOnly,
    isLimitedEditionHolding,
    limitedEditionHoldingOwners,
  ]);
  const shouldRenderLimitedEditionCard = useMemo(() => {
    if (!isLimitedEditionProduct) {
      return false;
    }

    if (limitedEditionIsPrivate && !isOwnerView) {
      return false;
    }

    return Boolean(
      editionNumber
      || limitedEditionSaleFallback
      || limitedEditionHoldingOwners.length
      || limitedEditionOwnersFiltered.length
      || ownershipDisplayName,
    );
  }, [
    editionNumber,
    isLimitedEditionProduct,
    limitedEditionHoldingOwners.length,
    limitedEditionIsPrivate,
    limitedEditionOwnersFiltered.length,
    limitedEditionSaleFallback,
    isOwnerView,
    ownershipDisplayName,
  ]);

  const soldHistoryEntries = useMemo(
    () => selectPreferredSoldHistoryEntries({
      isLimitedEditionProduct,
      isOriginalProduct,
      canUseMetadata: canUseMetadataSoldHistory,
      allowLimitedEditionMetadata: isOwnerView,
      transactionHistoryEntries,
      metadataSoldHistoryEntries,
    }),
    [
      canUseMetadataSoldHistory,
      isLimitedEditionProduct,
      isOriginalProduct,
      isOwnerView,
      metadataSoldHistoryEntries,
      transactionHistoryEntries,
    ],
  );

  const soldHistoryEntriesWithFallback = useMemo(() => {
    if (soldHistoryEntries.length > 0) {
      return soldHistoryEntries;
    }

    if (isLimitedEditionProduct && limitedEditionSaleFallback) {
      return [{
        id: 'limited-edition-sale-fallback',
        date: limitedEditionSaleFallback.soldAt ?? null,
        dateRaw: limitedEditionSaleFallback.soldAt ?? null,
        seller: 'ƒ?"',
        price: limitedEditionSaleFallback.price ?? null,
        currency: limitedEditionSaleFallback.currency ?? artwork_currency ?? null,
      }];
    }

    return soldHistoryEntries;
  }, [
    artwork_currency,
    isLimitedEditionProduct,
    limitedEditionSaleFallback,
    soldHistoryEntries,
  ]);

  const hasSoldHistoryEntries = soldHistoryEntriesWithFallback.length > 0;
  const shouldShowSoldHistory = !isLimitedEditionProduct && hasSoldHistoryEntries;
  const soldHistoryEntriesToDisplay = soldHistoryEntriesWithFallback;
  const openLimitedEdition = useCallback((selection = null) => {
    if (isOwnArtwork) {
      setSellInStoreOpen(true);
      return;
    }

    if (typeof onOpenLimitedEditionProp === 'function') {
      onOpenLimitedEditionProp(selection);
      return;
    }

    navigate('/artwork/purchase', {
      state: {
        artworkData,
        slug,
        typeOfPurchase: 'limited_edition',
        limitedEditionSelection: selection,
      },
    });
  }, [artworkData, isOwnArtwork, navigate, onOpenLimitedEditionProp, slug]);

  const handlePurchaseClick = useCallback(() => {
    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.PURCHASE_INTENT,
        metadata: { source: 'artwork-more-details__purchase-button' },
      });
    }

    if (isGalleryMember) {
      setShowPurchaseBlockedPopup(true);
      return;
    }

    if (isOwnArtwork) {
      setSellInStoreOpen(true);
      return;
    }

    if (typeof onOpenPurchase === 'function') {
      onOpenPurchase();
      return;
    }

    navigate('/artwork/purchase', {
      state: { artworkData, slug },
    });
  }, [artworkData, isGalleryMember, isOwnArtwork, navigate, onOpenPurchase, slug]);

  return (
    <div className="artwork-more-details">
      <div className="artwork-more-details__container">
        {/* Description */}
        <section className="artwork-more-details__section">
          {/* <h3 className="artwork-more-details__details-heading">Description</h3> */}
          <Description artworkData={artworkData} />
          {artworkAudio ? <AudioPlayer audio={artworkAudio} /> : null}
        </section>

        {shouldRenderLimitedEditionSummary ? (
          <>
            <hr className="artwork-more-details__divider" />
            <section className="artwork-more-details__section artwork-more-details__section--limited-edition">
              <LimitedEditionSummary
                editions={limitedEditionOptions}
                currency={currencyCode}
                onEditionSelect={(sizeKey, sizeData) => openLimitedEdition({ sizeKey, sizeData })}
                disabled={limitedEditionDisabled}
                onDisabledSelect={() => setLimitedEditionBlockedPopupOpen(true)}
                hideList={isAdminOnly}
                onShowOwners={openCollectors}
              />
            </section>
            <hr className="artwork-more-details__divider" />
          </>
        ) : (
          <hr className="artwork-more-details__divider" />
        )}

        {/* Artwork Details */}
        <section className="artwork-more-details__details">
          {/* <h3 className="artwork-more-details__details-heading">Artwork Details</h3> */}
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.title')}</span>
            <span className="artwork-more-details__detail-value">{displayArtworkTitle}</span>
          </div>
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.type')}</span>
            <span className="artwork-more-details__detail-value">{artworkTypeLabel}</span>
          </div>
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.medium')}</span>
            <span className="artwork-more-details__detail-value">{localizedArtworkMedium}</span>
          </div>
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.dimensions')}</span>
            <span className="artwork-more-details__detail-value">{formattedSize}</span>
          </div>
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.created_in')}</span>
            <span className="artwork-more-details__detail-value">{artwork_year}</span>
          </div>
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.signature')}</span>
            <span className="artwork-more-details__detail-value">{t('buy_details.artwork_values.signature_hand_signed')}</span>
          </div>
          <div className="artwork-more-details__detail-row">
            <span className="artwork-more-details__detail-label">{t('buy_details.artwork_labels.price')}</span>
            <span className="artwork-more-details__detail-value">
              {activeOfferPrice !== null ? (
                <ArtworkOfferPrice
                  originalPrice={artwork_price}
                  offerPrice={activeOfferPrice}
                  currency={activeOffer?.currency ?? currencyCode}
                />
              ) : (
                formattedPrice
              )}
            </span>
          </div>
        </section>

        <hr className="artwork-more-details__divider" />

        {/* Artist Block */}
        <section className="artwork-more-details__section">
          <h3 className="artwork-more-details__details-heading">{t('details.headings.artist')}</h3>
          <div className="profile-row">
            <UserProfileImage
              image={artist.image}
              typeOfProfile={artist.type || 'artist'}
              className="profile-image"
              onClick={openArtistArtworks}
            />
            <div className="profile-texts">
              <span
                className="profile-name"
                role="button"
                tabIndex={0}
                onClick={openArtistArtworks}
                onKeyDown={handleArtistKeyDown}
              >
                {artist.name || '-'}
              </span>
              <span className="profile-location">{artist.location || ''}</span>
            </div>
            {artist.id && (
              <FollowButton
                className="artwork-view__follow-button artwork-more-details__follow-button"
                userId={artist.id}
                followeeType={artist?.type === 'gallery_artist' ? 'gallery_artist' : 'user'}
                imFollowing={artist.imFollowing}
                isFollowingMe={artist.isFollowingMe}
                userName={artist.name}
                userUsername={artist.username}
                showFollowingIcon />
            )}
          </div>
        </section>

        <hr className="artwork-more-details__divider" />

        {/* Gallery Block */}
        <section className="artwork-more-details__section">
          <h3 className="artwork-more-details__details-heading">{ownershipHeadingDisplay}</h3>
          {isPrivateOwner ? (
            <PrivateOwnerIndicator label={privateOwnerLabel} />
          ) : (
            <div className="profile-row">
              <UserProfileImage
                image={ownershipProfileImage}
                typeOfProfile={ownershipProfileType}
                className={`profile-image${canNavigateToOwnershipProfile ? ' artwork-more-details__desktop__profile-image--clickable' : ''}`}
                onClick={canNavigateToOwnershipProfile ? handleOwnershipProfileClick : undefined}
              />
              <div className="profile-texts">
                <span
                  className={`profile-name${canNavigateToOwnershipProfile ? ' artwork-more-details__desktop__profile-name--link' : ''}`}
                  role={canNavigateToOwnershipProfile ? 'button' : undefined}
                  tabIndex={canNavigateToOwnershipProfile ? 0 : undefined}
                  onClick={canNavigateToOwnershipProfile ? handleOwnershipProfileClick : undefined}
                  onKeyDown={canNavigateToOwnershipProfile ? handleOwnershipProfileKeyDown : undefined}
                >
                  {ownershipDisplayName}
                </span>
                {ownershipLocation && (
                  <span className="profile-location">
                    {ownershipLocation}
                  </span>
                )}
              </div>
              {canFollowOwnership && ownershipUserId && (
                <FollowButton
                  className="artwork-view__follow-button artwork-more-details__follow-button"
                  userId={ownershipUserId}
                  imFollowing={ownershipImFollowing}
                  isFollowingMe={ownershipIsFollowingMe}
                  userName={ownershipDisplayName}
                  userUsername={ownershipUsername}
                  showFollowingIcon />
              )}
            </div>
          )}
        </section>

        <hr className="artwork-more-details__divider" />

        {(isLimitedEditionProduct ? shouldRenderLimitedEditionCard : shouldShowSoldHistory) ? (
          <>
            <section className="artwork-more-details__section artwork-more-details__section--sold-history">
              {isLimitedEditionProduct ? (
                <div className="artwork-more-details__limited-owner-card">
                  <LEsOwnershipCard
                    fallbackEditionNumber={editionNumber}
                    fallbackOwnerName={limitedEditionOwnerFallbackName}
                    fallbackOwnerImage={limitedEditionOwnerFallbackImage}
                    fallbackOwnerType={limitedEditionOwnerFallbackType}
                    fallbackPrice={limitedEditionFallbackPrice}
                    badgeLabelOverride={limitedEditionBadgeOverride}
                    loading={false}
                    isOwnerView={limitedEditionOwnerIsViewer}
                    viewerDisplayName={viewerDisplayName}
                    fallbackSoldAt={limitedEditionSoldAtFallback}
                  />
                </div>
              ) : (
                <div className="artwork-more-details__desktop__sold-history">
                  {/* <div className="artwork-more-details__desktop__sold-history-timeline">
                  <div className="artwork-more-details__desktop__sold-history-line" />
                  <div
                    className="artwork-more-details__desktop__sold-history-dots"
                    style={{ '--sold-history-dot-count': Math.max(1, soldHistoryEntriesToDisplay.length) }}
                  >
                    {soldHistoryEntriesToDisplay.map((entry, index) => (
                      <span
                        key={`${entry.id ?? index}-dot`}
                        className="artwork-more-details__desktop__sold-history-dot"
                      />
                    ))}
                  </div>
                </div> */}
                  <div className="artwork-more-details__desktop__sold-history-card">
                    <span className="artwork-more-details__desktop__sold-history-title">{t('details.headings.sold_history')}</span>
                    <div className="artwork-more-details__desktop__sold-history-table">
                      <div className="artwork-more-details__desktop__sold-history-row artwork-more-details__desktop__sold-history-row--head">
                        <span>{t('details.sold_history.columns.date')}</span>
                        <span>{t('details.sold_history.columns.from')}</span>
                        <span>{t('details.sold_history.columns.price')}</span>
                      </div>
                      {soldHistoryEntriesToDisplay.map((entry, index) => {
                        const key = entry.id ?? `${entry.date ?? 'date'}-${index}`;
                        const sellerProfile = entry.sellerProfile ?? null;
                        const sellerName = sellerProfile?.name ?? entry.seller ?? '—';
                        const sellerUsername = sellerProfile?.username ?? null;
                        const sellerImageId = sellerProfile?.image_id ?? null;
                        const sellerType = sellerProfile?.type ?? 'collector';

                        const handleSellerNavigate = () => {
                          if (!sellerUsername) {
                            return;
                          }
                          navigate(`/user/${sellerUsername}`);
                        };

                        const sellerContent = sellerProfile ? (
                          sellerUsername ? (
                            <button
                              type="button"
                              className="artwork-more-details__desktop__sold-history-user"
                              onClick={handleSellerNavigate}
                            >
                              <UserProfileImage
                                image={sellerImageId}
                                typeOfProfile={sellerType}
                                className="artwork-more-details__desktop__sold-history-avatar"
                              />
                              <span className="artwork-more-details__desktop__sold-history-user-name">
                                {sellerName}
                              </span>
                            </button>
                          ) : (
                            <span className="artwork-more-details__desktop__sold-history-user artwork-more-details__desktop__sold-history-user--static">
                              <UserProfileImage
                                image={sellerImageId}
                                typeOfProfile={sellerType}
                                className="artwork-more-details__desktop__sold-history-avatar"
                              />
                              <span className="artwork-more-details__desktop__sold-history-user-name">
                                {sellerName}
                              </span>
                            </span>
                          )
                        ) : (
                          <span className="artwork-more-details__desktop__sold-history-user artwork-more-details__desktop__sold-history-user--static">
                            <span className="artwork-more-details__desktop__sold-history-user-name">
                              {sellerName}
                            </span>
                          </span>
                        );

                        return (
                          <div key={key} className="artwork-more-details__desktop__sold-history-row">
                            <span>{formatSoldHistoryDate(entry.date, entry.dateRaw)}</span>
                            <div className="artwork-more-details__desktop__sold-history-seller">
                              {sellerContent}
                            </div>
                            <span>{formatSoldHistoryPrice(entry.price, entry.currency)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>
            <hr className="artwork-more-details__divider" />
          </>
        ) : null}

        {/* Certificates / Frame (hidden for holding views) */}
        {!holdingType && (
          <section className="artwork-more-details__extra-info">
            {artwork_includes_certificate && (
              <div className="artwork-more-details__extra-info-row">
                <CertificateOfAuthenticityIcon className="artwork-more-details__extra-info-icon" />
                <span className="artwork-more-details__extra-info-text">
                  {`${t('details.extra_info.includes')} `}
                  {t('details.certificate.certificate_of_authenticity')}
                  {` ${t('details.extra_info.issued_by_gallery_or_artist')}`}
                </span>
              </div>
            )}
            <div className="artwork-more-details__extra-info-row">
              <CertificateOfAuthenticityIcon className="artwork-more-details__extra-info-icon" />
              <span className="artwork-more-details__extra-info-text">
                {`${t('details.extra_info.includes')} `}
                {renderCertificateOwnershipLink(t('details.certificate.certificate_of_ownership'))}
                {` ${t('details.extra_info.provided_by_suarte')}`}
              </span>
            </div>
            {artwork_includes_frame && (
              <div className="artwork-more-details__extra-info-row">
                <AlreadyFramedArtworkIcon className="artwork-more-details__extra-info-icon" />
                <span className="artwork-more-details__extra-info-text">
                  {t('details.extra_info.already_framed')}
                </span>
              </div>
            )}
          </section>
        )}

        {artistModalData && (
          <ArtworksOfArtist
            open={isArtistArtworksOpen}
            close={() => setIsArtistArtworksOpen(false)}
            artist={artistModalData}
            profileData={artistModalData}
            internal={false}
            showHeader={false}
            cacheKey={modalQueryData.cacheKey}
            queryEndpoint={modalQueryData.endpoint}
          />
        )}
      </div>
      {/* Sticky Purchase Button */}
      {!isAuctionArtwork && canPurchase && !isAdminOnly && (
        <div className="artwork-more-details__footer-navbar">
          <div className="artwork-more-details__footer-info">
            <span className="artwork-more-details__footer-label">{t('buy_details.artwork_labels.price')}</span>
            <span className="artwork-more-details__footer-value">
              {activeOfferPrice !== null ? (
                <ArtworkOfferPrice
                  originalPrice={artwork_price}
                  offerPrice={activeOfferPrice}
                  currency={activeOffer?.currency ?? currencyCode}
                />
              ) : (
                formattedPrice
              )}
            </span>
          </div>
          <button
            className="artwork-more-details__purchase-button"
            onClick={handlePurchaseClick}
          >
            {isOwnArtwork ? 'Sell In-store' : t('purchase_footer.default_button_label')}
          </button>
        </div>
      )}
      <SellInStorePopup
        open={sellInStoreOpen}
        close={() => setSellInStoreOpen(false)}
        artworkData={artworkData}
        onSuccess={(updated) => {
          if (updated) {
            setArtworkData(updated);
          }
        }}
      />
      <ActionRejectedPopup
        open={showPurchaseBlockedPopup}
        close={() => setShowPurchaseBlockedPopup(false)}
        description={t('purchase_footer.errors.gallery_member_purchase_blocked')}
      />
      <ActionRejectedPopup
        open={limitedEditionBlockedPopupOpen}
        close={() => setLimitedEditionBlockedPopupOpen(false)}
        description={limitedEditionDisabledMessage}
      />
    </div>
  );
}
